import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';

const fmtDate = (d) => {
  if (!d) return '';
  const dateStr = d.includes('T') ? d.split('T')[0] : d;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const fmtTime = (t) => {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const Avatar = ({ initials, size = 40 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    background: '#0d2b1a', color: '#7ec99a',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.35, fontWeight: 600, flexShrink: 0,
  }}>
    {initials}
  </div>
);

const FriendCard = ({ friend, onFollowToggle, defaultExpanded }) => {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(!!defaultExpanded);

  // Open if URL signals it after first render
  useEffect(() => {
    if (defaultExpanded) setExpanded(true);
  }, [defaultExpanded]);

  const handleFollow = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      if (friend.is_following) {
        await api.unfollowUser(friend.id);
      } else {
        await api.followUser(friend.id);
      }
      onFollowToggle(friend.id, !friend.is_following);
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar initials={friend.initials} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500, fontSize: 14, color: '#0d2b1a' }}>{friend.name}</div>
          <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 1, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {friend.handicap != null && (
              <span>HCP {parseFloat(friend.handicap).toFixed(1)}</span>
            )}
            {friend.ghin_number && (
              <span style={{ color: '#2e7d4f' }}>GHIN {friend.ghin_number}</span>
            )}
            {friend.mutual_groups?.length > 0 && (
              <span>{friend.mutual_groups.join(' · ')}</span>
            )}
          </div>
        </div>
        <button
          className={`btn btn-xs ${friend.is_following ? 'btn-outline' : 'btn-accent'}`}
          onClick={handleFollow}
          disabled={busy}
          style={{ flexShrink: 0 }}
        >
          {busy ? '…' : friend.is_following ? 'Following' : '+ Follow'}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0ede4' }}>
          {friend.phone && (
            <div style={{ fontSize: 12, color: '#4a4a3a', marginBottom: 6 }}>
              📱 <a href={`tel:${friend.phone}`} style={{ color: '#0d2b1a' }}>{friend.phone}</a>
            </div>
          )}
          {/* Shared email — only present when the friend opted into sharing it.
              mailto: opens their mail client; the copy button is a fallback for
              when mailto isn't wired up (common on desktop). */}
          {friend.shared_email && (
            <div style={{ fontSize: 12, color: '#4a4a3a', marginBottom: 6,
                          display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>✉️</span>
              <a href={`mailto:${friend.shared_email}`} style={{ color: '#0d2b1a' }}>
                {friend.shared_email}
              </a>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(friend.shared_email).catch(() => {});
                  }
                }}
                style={{
                  background: '#f4f1eb', border: '1px solid #d8d4c8',
                  borderRadius: 6, fontSize: 10, color: '#5a5a4a',
                  padding: '1px 6px', cursor: 'pointer',
                }}
                title="Copy email address"
              >
                Copy
              </button>
            </div>
          )}
          {friend.upcoming_tee_times?.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, color: '#9a9a8a', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>
                Upcoming tee times
              </div>
              {friend.upcoming_tee_times.map(tt => (
                <div key={tt.id} style={{ fontSize: 12, color: '#4a4a3a', padding: '4px 0', borderBottom: '0.5px solid #f0ede4' }}>
                  <strong>{tt.course_name}</strong> · {fmtDate(tt.tee_date)} at {fmtTime(tt.tee_time)}
                  <span style={{ color: '#2e7d4f', marginLeft: 6 }}>
                    {tt.total_spots - tt.player_count} spot{tt.total_spots - tt.player_count !== 1 ? 's' : ''} open
                  </span>
                </div>
              ))}
            </div>
          )}
          {!friend.phone && !friend.shared_email && (!friend.upcoming_tee_times || friend.upcoming_tee_times.length === 0) && (
            <div style={{ fontSize: 12, color: '#9a9a8a' }}>No upcoming tee times.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default function FriendsPage() {
  const [friends, setFriends] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchParams] = useSearchParams();
  const targetUserId = searchParams.get('user') || null;
  const targetCardRef = useRef(null);
  // Brief visual pulse on the targeted card so the eye lands on it after the
  // scroll — especially helps when several cards look similar. Clears itself.
  const [highlightId, setHighlightId] = useState(null);

  // Scroll to and highlight the target friend card after friends load
  useEffect(() => {
    if (!targetUserId || loading) return;
    if (targetCardRef.current) {
      targetCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(targetUserId);
      const t = setTimeout(() => setHighlightId(null), 2200);
      return () => clearTimeout(t);
    }
  }, [targetUserId, loading, friends.length]);

  const load = useCallback(async () => {
    try {
      const data = await api.getFriends();
      // For each friend, also load their upcoming tee times
      const enriched = await Promise.all(data.map(async (f) => {
        try {
          const detail = await api.getFriend(f.id);
          return { ...f, upcoming_tee_times: detail.upcoming_tee_times, phone: detail.phone };
        } catch { return f; }
      }));
      setFriends(enriched);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await api.searchFriends(query);
        setSearchResults(results);
      } catch (err) { console.error(err); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const handleFollowToggle = (userId, nowFollowing) => {
    setFriends(prev => prev.map(f =>
      f.id === userId ? { ...f, is_following: nowFollowing } : f
    ));
    setSearchResults(prev => prev.map(f =>
      f.id === userId ? { ...f, is_following: nowFollowing } : f
    ));
    if (nowFollowing) load(); // reload to include newly followed person
  };

  const displayList = query.length >= 2 ? searchResults : friends;

  return (
    <div>
      <div style={{ marginBottom: '1.1rem' }}>
        <h2 style={{ fontSize: 22 }}>Friends</h2>
        <p style={{ fontSize: 12, color: '#7a7a6a', marginTop: 1 }}>
          Your golf network — people you've played with, share groups with, or follow
        </p>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '1rem' }}>
        <input
          className="form-input"
          type="text"
          placeholder="Search by name or email…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{ paddingLeft: 36 }}
        />
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>🔍</span>
        {searching && (
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9a9a8a' }}>
            Searching…
          </span>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="empty-state"><p>Loading…</p></div>
      ) : displayList.length === 0 ? (
        <div className="empty-state">
          {query.length >= 2 ? (
            <>
              <p style={{ fontSize: 24 }}>🔍</p>
              <p>No users found for "{query}"</p>
            </>
          ) : (
            <>
              <p style={{ fontSize: 28 }}>👥</p>
              <p>Your network is empty for now.</p>
              <p style={{ fontSize: 12, color: '#9a9a8a' }}>
                Post a tee time, join a group, or search for golfers above to get started.
              </p>
            </>
          )}
        </div>
      ) : (
        <>
          {query.length >= 2 && (
            <div style={{ fontSize: 11, color: '#9a9a8a', marginBottom: 8 }}>
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{query}"
            </div>
          )}
          {query.length < 2 && (
            <div style={{ fontSize: 11, color: '#9a9a8a', marginBottom: 8 }}>
              {friends.length} in your network
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
            {displayList.map(f => (
              <div
                key={f.id}
                ref={f.id === targetUserId ? targetCardRef : null}
                style={{
                  borderRadius: 12,
                  transition: 'box-shadow 0.4s ease, background 0.4s ease',
                  boxShadow: highlightId === f.id
                    ? '0 0 0 3px #7ec99a, 0 4px 14px rgba(46,125,79,0.25)'
                    : 'none',
                }}
              >
                <FriendCard
                  friend={f}
                  onFollowToggle={handleFollowToggle}
                  defaultExpanded={f.id === targetUserId}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
